// Holds the user's QR-scan intent across login / register / install /
// reload. The /go route writes here when the visitor is unauth'd; the
// auth flow consumes it on first successful login. 30-minute TTL keeps
// stale intents from re-triggering after the user has moved on.

const STORAGE_KEY = 'wesafe.pendingSignon';
const PATH_STORAGE_KEY = 'wesafe.pendingPath';
const TTL_MS = 30 * 60 * 1000;

export interface PendingIntent {
  jobsiteId: number;
  jobsiteCode: string;
  jobsiteName: string;
  // Where the /go branch decided to land the user post-auth.
  // F-4 will resolve this to a concrete page.
  action: 'sign-on' | 'sign-off' | 'switch';
  storedAt: number;
}

export function savePendingIntent(intent: Omit<PendingIntent, 'storedAt'>): void {
  try {
    const payload: PendingIntent = { ...intent, storedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Private mode or quota — accept the loss.
  }
}

export function peekPendingIntent(): PendingIntent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingIntent;
    if (!parsed?.storedAt || Date.now() - parsed.storedAt > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function consumePendingIntent(): PendingIntent | null {
  const intent = peekPendingIntent();
  if (intent) clearPendingIntent();
  return intent;
}

export function clearPendingIntent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Raw-path intent: a logged-out QR scan only has the opaque publicId in the
// URL and can't call the authed resolve endpoint, so the structured
// PendingIntent (numeric id + name) doesn't fit. We instead persist the full
// requested `/m/...` path and replay it verbatim after login / onboarding.
interface PendingPath {
  path: string;
  storedAt: number;
}

export function setPendingPath(path: string): void {
  try {
    const payload: PendingPath = { path, storedAt: Date.now() };
    localStorage.setItem(PATH_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Private mode or quota — accept the loss.
  }
}

export function peekPendingPath(): string | null {
  try {
    const raw = localStorage.getItem(PATH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingPath;
    if (!parsed?.path || !parsed?.storedAt || Date.now() - parsed.storedAt > TTL_MS) {
      localStorage.removeItem(PATH_STORAGE_KEY);
      return null;
    }
    return parsed.path;
  } catch {
    return null;
  }
}

export function consumePendingPath(): string | null {
  const path = peekPendingPath();
  if (path) clearPendingPath();
  return path;
}

export function clearPendingPath(): void {
  try {
    localStorage.removeItem(PATH_STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Build the URL the user should land at after a pending intent is consumed.
// Kept here so the post-auth redirect and the /go branch agree on shape.
export function pendingIntentToPath(intent: PendingIntent): string {
  switch (intent.action) {
    case 'sign-on':
      return `/m/sign-on/${intent.jobsiteId}`;
    case 'sign-off':
      return `/m/sign-off/${intent.jobsiteId}`;
    case 'switch':
      return `/m/switch-sites/${intent.jobsiteId}`;
    default:
      return '/';
  }
}
